import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BRANCH_CODES, BRANCH_LABELS } from '@lorsamthai/ops-shared';
import { getStockDetail, type SkuDetail } from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtInt, fmtDate, daysAgo } from '../lib/format.js';
import { useAuth } from '../auth/context.js';
import TargetsEditor from './TargetsEditor.js';

export default function StockDetail() {
  const { sku = '' } = useParams();
  const { me } = useAuth();
  const [detail, setDetail] = useState<SkuDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getStockDetail(sku).then(setDetail).catch((e) => setError(e.message));
  }, [sku]);

  if (error) {
    return <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>;
  }
  if (!detail) {
    return <div className="text-sm text-slate-500">กำลังโหลด...</div>;
  }

  // Build per-branch row combining target + effective
  const byBranch = BRANCH_CODES.map((b) => {
    const target = detail.targets.find((t) => t.branch === b);
    const eff = detail.effective.find((e) => e.branch === b);
    return { branch: b, target, eff };
  });

  // bp's spare capacity = effective(bp) - min(bp); used to suggest TR qty for child branches.
  // นับเฉพาะยาง DOT ปีปัจจุบัน ให้ตรงกับเกณฑ์ของหน้า Order/Transfer Day.
  const bpRow = byBranch.find((r) => r.branch === 'bp');
  const bpAvail = Math.max(
    (bpRow?.eff?.effective_cy ?? 0) - (bpRow?.target?.min_qty ?? 0),
    0
  );

  // Aggregate pending PO qty by dest branch
  const pendingByBranch = new Map<string, number>();
  for (const entry of detail.pending_pos) {
    const po = entry.purchase_orders[0];
    if (!po) continue;
    const pending = entry.qty_ordered - entry.qty_received;
    if (pending <= 0) continue;
    pendingByBranch.set(po.dest_branch, (pendingByBranch.get(po.dest_branch) ?? 0) + pending);
  }

  return (
    <div className="space-y-5">
      <div>
        <Link to="/stock" className="text-xs text-slate-500 hover:underline">
          ← กลับรายการ stock
        </Link>
        <h1 className="mt-1 font-mono text-lg font-bold">{sku}</h1>
      </div>

      <section className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">สาขา</th>
              <th className="px-3 py-2 text-right">ในสต็อก</th>
              <th className="px-3 py-2 text-right">กำลังมา</th>
              <th className="px-3 py-2 text-right">Effective</th>
              <th
                className="px-3 py-2 text-right"
                title="นับเฉพาะยาง DOT ปีปัจจุบัน — ตัวเลขที่ใช้ตัดสินใจสั่ง/โอน"
              >
                DOT ปีนี้
              </th>
              <th className="px-3 py-2 text-right">Min / Target</th>
              <th className="px-3 py-2">สถานะ</th>
              {me?.role === 'super_admin' && <th className="px-3 py-2">ลัด</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {byBranch.map(({ branch, target, eff }) => {
              const effective = eff?.effective ?? 0;
              // ยาง DOT ปีปัจจุบัน = ตัวเลขที่ใช้ตัดสินว่าต่ำกว่า min ไหม / ต้องเติมเท่าไหร่
              const effectiveCy = eff?.effective_cy ?? 0;
              const oldStock = eff?.on_hand_old ?? 0;
              const min = target?.min_qty ?? 0;
              const targetQty = target?.target_qty ?? 0;
              const low = effectiveCy < min;
              const need = Math.max(targetQty - effectiveCy, 0);
              const isHub = branch === 'bp';
              // For child branches: suggest TR from bp, capped by bp_avail
              const trQty = !isHub && low ? Math.min(need, bpAvail) : 0;
              // For bp itself: suggest PO equal to need
              const poQty = isHub && low ? need : 0;
              return (
                <tr key={branch}>
                  <td className="px-3 py-2">
                    {BRANCH_LABELS[branch]}
                    {isHub && <span className="ml-1 text-xs text-slate-400">(hub)</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(eff?.on_hand ?? 0)}</td>
                  <td className="px-3 py-2 text-right">
                    {(eff?.incoming_po ?? 0) > 0 ? (
                      <Badge tone="info">+{eff?.incoming_po}</Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(effective)}</td>
                  <td
                    className="px-3 py-2 text-right font-mono font-semibold"
                    title={oldStock > 0 ? `DOT ปีเก่าค้าง ${oldStock} เส้น` : undefined}
                  >
                    {fmtInt(effectiveCy)}
                    {oldStock > 0 && (
                      <span className="ml-1 text-xs font-normal text-amber-700">
                        (เก่า {fmtInt(oldStock)})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">
                    {fmtInt(min)} / {fmtInt(targetQty)}
                  </td>
                  <td className="px-3 py-2">
                    {effectiveCy === 0 ? (
                      <Badge tone="danger">หมด</Badge>
                    ) : low ? (
                      <Badge tone="low">ต่ำกว่า min</Badge>
                    ) : (
                      <Badge tone="ok">ปกติ</Badge>
                    )}
                  </td>
                  {me?.role === 'super_admin' && (
                    <td className="px-3 py-2 text-xs">
                      {trQty > 0 ? (
                        <Link
                          to={`/transfers/new?from=bp&to=${branch}&sku=${encodeURIComponent(sku)}&qty=${trQty}`}
                          className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-800 hover:bg-emerald-100"
                          title={`bp มี ${eff?.incoming_po === undefined ? '' : ''}${bpAvail} เส้นพร้อมโอน`}
                        >
                          🚚 โอน {trQty}
                        </Link>
                      ) : poQty > 0 ? (
                        <Link
                          to={`/pos/new?dest=bp&sku=${encodeURIComponent(sku)}&qty=${poQty}`}
                          className="rounded-md border border-slate-300 bg-slate-50 px-2 py-1 text-slate-800 hover:bg-slate-100"
                        >
                          🛒 สั่ง {poQty}
                        </Link>
                      ) : !isHub && low && bpAvail === 0 ? (
                        <Link
                          to={`/pos/new?dest=${branch}&sku=${encodeURIComponent(sku)}&qty=${need}`}
                          className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800 hover:bg-amber-100"
                          title="bp ไม่มียางเหลือ — ต้องสั่ง supplier ส่งตรงสาขานี้"
                        >
                          🛒 สั่งตรง {need}
                        </Link>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {me?.role === 'super_admin' && (
        <div className="flex flex-wrap gap-2 text-sm">
          <Link
            to={`/pos/new?sku=${encodeURIComponent(sku)}`}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50"
          >
            + สร้าง PO เอง
          </Link>
          <Link
            to={`/transfers/new?sku=${encodeURIComponent(sku)}`}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50"
          >
            + สร้างใบโอนเอง
          </Link>
        </div>
      )}

      {detail.pending_pos.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold">PO ที่ยังรออยู่</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {detail.pending_pos.map((p, i) => {
              const po = p.purchase_orders[0];
              if (!po) return null;
              const age = daysAgo(po.ordered_at);
              return (
                <li key={i} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                  <div>
                    <span className="font-mono text-xs">{po.po_ref ?? `#${po.id}`}</span>
                    <span className="ml-2 text-xs text-slate-500">→ {BRANCH_LABELS[po.dest_branch]}</span>
                  </div>
                  <div className="text-xs text-slate-600">
                    {fmtInt(p.qty_ordered - p.qty_received)}/{fmtInt(p.qty_ordered)} เส้น
                    {age !== null && <span className="ml-2 text-slate-500">· สั่งเมื่อ {age} วันก่อน</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {me?.role === 'super_admin' && <TargetsEditor sku={sku} />}
    </div>
  );
}
